import {
	afterNextRender,
	ChangeDetectionStrategy,
	Component,
	DestroyRef,
	effect,
	ElementRef,
	EventEmitter,
	Injector,
	Input,
	OnInit,
	Output,
	inject,
	viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';
import { finalize, TimeoutError } from 'rxjs';
import { InlineLoginService } from '../shared/services/inline-login.service';
import { TokenIntrospectionService } from '../shared/services/token-introspection.service';
import { SipAccountOrderService } from '../shared/services/sip-account-order.service';
import { SipOrderLinkService } from '../shared/services/sip-order-link.service';
import { ORDER_ERROR_ADDRESS_INVALID, SipOrderTarget } from '../shared/models/sip-account-order.model';
import { AuthTokenStore } from '../shared/services/auth-token.store';
import { GuidedSetupStore, SetupPhase } from './guided-setup.store';
import { LoggingService } from '../shared/services/logging.service';
import { LegalLinksService } from '../shared/services/legal-links.service';
import { renderLegalLinks } from '../shared/helpers/legal-links.helper';
import { LOG_EVENTS } from '../shared/services/log-events';
import {
	deriveSetupRequirement,
	isNumberSetWithoutSipAccount,
	isPlausiblePhoneNumber,
	normalizePhoneNumber,
	SetupSignals,
	SetupStep,
} from '../shared/helpers/guided-setup.helper';
import { OutgoingNumberDTO } from '../shared/models/callback-on-callnote.model';
import { isMockMode } from '../shared/testing/mock-mode';
import { LocalizationService } from '../shared/services/localization.service';
import { I18nKeys, I18nSipSetupEnum } from '../shared/models/i18n.model';
import { fillPlaceholders } from '../shared/helpers/i18n-placeholders.helper';
import { CliVerificationService } from '../shared/services/cli-verification.service';
import { numberStepVerdict } from '../shared/helpers/cli-verification-outcome.helper';
import { precheckPhoneNumber } from '../shared/helpers/phone-number-precheck.helper';
import { CLI_MANUAL_REQUEST_NOTE_MAX_LENGTH } from '../shared/models/cli-verification.model';
import { GuidedSetupVerification } from './guided-setup-verification';

/**
 * TEMPORARY (mock harness) — what the sign-in step is pre-filled with while reviewing. Obvious
 * placeholders on purpose: nobody should wonder for a second whether these are real credentials.
 */
export const MOCK_LOGIN = { username: 'mock.user', password: 'mock-password' } as const;

/**
 * What the customer gets, in the order it happens: dedicated number, contract extension, display
 * number (corrected 2026-09-11 - the third line used to describe a one-off setup that is not what
 * is booked).
 *
 * Rendered with neutral markers, not ticks. On the intro nothing has happened yet, and a tick reads
 * as "done".
 */
export const INCLUDED_ITEMS: readonly I18nSipSetupEnum[] = [
	I18nSipSetupEnum.SIP_ORDER_INCLUDED_CONNECTION,
	I18nSipSetupEnum.SIP_ORDER_INCLUDED_CONTRACT_EXTENSION,
	I18nSipSetupEnum.SIP_ORDER_INCLUDED_DISPLAY_NUMBER,
];

/** How the wizard was opened: the callnote dialog, or the SIP order link (no sign-in, no number step). */
export type SetupEntry = 'selfService' | 'link';

/** The link path's included list: no display-number bullet, because that path collects no number. */
export const LINK_INCLUDED_ITEMS: readonly I18nSipSetupEnum[] = INCLUDED_ITEMS.filter(
	(item) => item !== I18nSipSetupEnum.SIP_ORDER_INCLUDED_DISPLAY_NUMBER,
);

/**
 * PS-6495 — the guided setup a customer walks through when no usable outgoing caller ID exists.
 *
 * Hosted in a dialog (`GuidedSetupDialog`), which is the frame of the workflow from the moment the
 * customer presses the call button. State lives in `GuidedSetupStore`, **not here**: cancelling the
 * dialog has to reopen at the same place, and a component destroyed on close cannot hold that.
 *
 * `ready` means the callback may now be created — the dialog closes and the parent triggers it with
 * no further click.
 */
@Component({
	selector: 'app-guided-setup',
	templateUrl: './guided-setup.html',
	styleUrls: [
		'./guided-setup.scss',
		'./guided-setup-fields.scss',
		'./guided-setup-order.scss',
		'./guided-setup-verify.scss',
		'./guided-setup-link.scss',
	],
	standalone: false,
	// Component-scoped on purpose: the verification code must die with the dialog.
	providers: [GuidedSetupVerification],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GuidedSetup implements OnInit {
	/** The three payload fields that decide what is missing. */
	@Input({ required: true }) signals!: SetupSignals;

	/** Candidates for the number field. Rendered as delivered — the backend owns the list (D8). */
	@Input() availableNumbers: OutgoingNumberDTO[] = [];

	/**
	 * The monthly price, formatted by the parent from the payload's price and currency
	 * (`formatMonthlyPrice`). Null when no figure may be shown, and then the order screen says so
	 * instead of inventing one — the screen where a contract is concluded is the worst possible
	 * place to guess.
	 */
	@Input() monthlyCost: string | null = null;
	/**
	 * False when the plan does not carry the SIP extension. Defaults to true so a payload from
	 * before PS-6526 keeps behaving as it did rather than silently withdrawing the offer.
	 */
	@Input() sipOnOffer = true;

	/**
	 * The page's language tag (from the callnote's `languageId`), for times shown on screen - the
	 * same rule as the price (MI3): the page's language, not the browser's.
	 */
	@Input() locale: string | null = null;

	/** How the wizard was opened; the callnote dialog leaves the default. */
	@Input() entry: SetupEntry = 'selfService';

	/**
	 * PS-6646 — the phone service the callnote belongs to. A sign-in whose token resolves to any
	 * other one is refused, so nothing is ever ordered or set on the wrong account. Null on the link
	 * entry, which has no callnote, and from a document-api that does not send it yet.
	 */
	@Input() expectedPhoneServiceId: number | null = null;

	/** Hides the step rail and progress line. */
	@Input() hideStepRail = false;

	/** Setup is complete; the payload is the number to signal. */
	@Output() ready = new EventEmitter<string>();

	/** The customer gave up on the run entirely. */
	@Output() dismissed = new EventEmitter<void>();

	/** Close the dialog but keep the run, so reopening resumes at the same step. */
	@Output() suspended = new EventEmitter<void>();

	/** Unrecoverable. The payload is a diagnostic reason, never shown to the customer. */
	@Output() failed = new EventEmitter<string>();

	/**
	 * The run ends and the service has to follow it up: the parent creates the callback WITHOUT a
	 * number and with `requestContactForBookingNumber` set - the same task the old `contact_us`
	 * path produced. Emitted when a refused or unconfirmed booking is acknowledged, and when the
	 * customer asked for it after a sign-in problem.
	 */
	@Output() contactService = new EventEmitter<void>();

	public readonly store: GuidedSetupStore = inject(GuidedSetupStore);
	public readonly I18N = I18nKeys;

	/**
	 * The sign-in form. Plain fields, not a FormGroup: there is nothing to validate beyond "not
	 * empty", and the proxy is the only authority on whether the pair is right.
	 *
	 * Deliberately on the component and not in the store - see `resetLoginForm`.
	 */
	public username = '';
	public password = '';
	public twoFactorCode = '';

	private readonly destroyRef: DestroyRef = inject(DestroyRef);
	private readonly inlineLogin: InlineLoginService = inject(InlineLoginService);
	private readonly introspection: TokenIntrospectionService = inject(TokenIntrospectionService);
	private readonly orderService: SipAccountOrderService = inject(SipAccountOrderService);
	private readonly sipOrderLink: SipOrderLinkService = inject(SipOrderLinkService);
	private readonly tokenStore: AuthTokenStore = inject(AuthTokenStore);
	private readonly logger: LoggingService = inject(LoggingService);
	private readonly legalLinks: LegalLinksService = inject(LegalLinksService);
	private readonly localization: LocalizationService = inject(LocalizationService);
	private readonly cliVerification: CliVerificationService = inject(CliVerificationService);
	public readonly verification: GuidedSetupVerification = inject(GuidedSetupVerification);
	public readonly manualNoteMaxLength = CLI_MANUAL_REQUEST_NOTE_MAX_LENGTH;
	/** The optional note for a manual verification request. Free text, so never logged. */
	public manualNote = '';

	/**
	 * The translations as a signal, read by `translate` so a template binding that goes through it
	 * re-renders when the payload's translations land. OnPush and zoneless: without a tracked read,
	 * a getter evaluated before they arrive would keep its empty answer.
	 */
	private readonly translations = toSignal(this.localization.translations$, { initialValue: null });

	/**
	 * The current step's heading (`#stepHeading`, `tabindex="-1"`). Only one phase renders at a
	 * time, so there is only ever one.
	 */
	private readonly stepHeading = viewChild<ElementRef<HTMLElement>>('stepHeading');
	private readonly numberInput = viewChild<ElementRef<HTMLInputElement>>('numberInput');
	private readonly numberConfirm = viewChild<ElementRef<HTMLButtonElement>>('numberConfirm');
	private readonly injector = inject(Injector);

	/**
	 * H6 (code review 2): the phase change destroys the button that had focus, which dropped focus
	 * onto `<body>` - behind the modal. After every phase change, once the new step has rendered,
	 * focus goes to its heading, so keyboard and screen-reader customers land on the new step.
	 */
	constructor() {
		effect(() => {
			const phase = this.store.phase();
			afterNextRender(() => this.focusStep(phase), { injector: this.injector });
		});
		this.verification.unauthorized$.pipe(takeUntilDestroyed()).subscribe(() => this.requireNewSignIn());
	}

	/**
	 * The number step is a form, not a page to read (Torsten, 2026-10-14): focus goes where the
	 * customer acts next - the empty field, or "Continue" when a number is already there. Every
	 * other step keeps the heading (H6). Falls back to the heading when neither is on screen, e.g.
	 * a candidate list with nothing picked yet.
	 */
	private focusStep(phase: SetupPhase): void {
		if (phase === 'chooseNumber') {
			const target =
				this.signalledNumber !== null ? this.numberConfirm()?.nativeElement : this.numberInput()?.nativeElement;
			if (target && !(target as HTMLButtonElement).disabled) {
				target.focus();
				return;
			}
		}
		this.stepHeading()?.nativeElement.focus();
	}

	/**
	 * L9: whether the number field has been left once. The format error waits for it, so its
	 * `role="alert"` does not fire on the first keystroke.
	 */
	public numberTouched = false;

	ngOnInit(): void {
		// Only for a fresh run. Resuming after a cancelled dialog must not overwrite what was entered.
		if (!this.store.active()) {
			this.store.outgoingNumber.set(this.signals.outboundOutgoingPhoneNumber?.trim() ?? '');
			this.store.displaySteps.set(deriveSetupRequirement(this.signals).steps);
		} else if (this.store.phase() === 'signIn') {
			// Resumed on the sign-in step. The credentials lived on the component that is gone, but
			// `twoFactorRequired` lives in the root store and survived: without this the code field
			// renders above an empty username and password. The server asks for the second factor
			// again when it needs it.
			this.resetLoginForm();
		}
		// A run suspended mid-call comes back without the code - it died with the last dialog. The
		// verify screen re-triggers, and the server hands back the same code if the call still runs.
		if (this.store.phase() === 'verifyRunning') {
			this.store.phase.set('verify');
		}
		// The confirmation's countdown died with the last dialog too; its next stop is the overview.
		if (this.store.phase() === 'verified') {
			this.store.phase.set('booked');
		}
	}

	/** True on the customer-service SIP order link. */
	get isLinkEntry(): boolean {
		return this.entry === 'link';
	}

	/** What the order summary lists as included; a getter because a field initializer runs before `entry` is set. */
	get includedItems(): readonly I18nSipSetupEnum[] {
		return this.isLinkEntry ? LINK_INCLUDED_ITEMS : INCLUDED_ITEMS;
	}

	/** Whether the header has anything to show. */
	get hasHeaderContent(): boolean {
		return !this.isLinkEntry || !this.hideStepRail;
	}

	/** The rail and progress line: not on the link page, and not on a stopped flow. */
	get showsProgress(): boolean {
		const phase = this.store.phase();
		return (
			!this.hideStepRail && this.totalSteps > 1 && phase !== 'failed' && phase !== 'serviceFollowUp'
		);
	}

	/** Whether the footer carries a button; on the link page only the order step has one. */
	get hasFooterActions(): boolean {
		if (!this.isLinkEntry) {
			return true;
		}
		return this.store.phase() === 'order';
	}

	get requirement() {
		const base = deriveSetupRequirement(this.signals);
		if (!this.store.sipOrdered()) {
			return base;
		}
		return {
			...base,
			needsSipAccount: false,
			needsConsent: false,
			steps: base.steps.filter((step) => step !== 'orderSipAccount'),
		};
	}

	get isSignedIn(): boolean {
		return this.tokenStore.hasToken;
	}

	/** Human position in the flow, for the dialog's own progress line. */
	get currentStepIndex(): number {
		const steps = this.store.displaySteps();
		const index = steps.findIndex((step) => this.isStepCurrent(step));
		return index < 0 ? 0 : index;
	}

	get totalSteps(): number {
		return this.store.displaySteps().length;
	}

	/**
	 * True while the field still holds the number the payload arrived with. Drives the confirmation
	 * line: the customer should see that this is already set up rather than wonder why a step is
	 * asking for something they have. Goes away the moment they type, because then it is no longer
	 * what is configured.
	 */
	get numberIsPreconfigured(): boolean {
		const configured = this.signals.outboundOutgoingPhoneNumber?.trim() ?? '';
		return configured.length > 0 && this.store.outgoingNumber().trim() === configured;
	}

	/**
	 * The number the order will signal, or null while it is still to be chosen.
	 *
	 * H4 (PS-6495 code review 2): the entered number is only signalled once it is VERIFIED; until
	 * then the SIP number is, and the overview follows that. `sip_number_hint` and
	 * `sip_order_signalled_number_label` still call it the signalled number before it is proven -
	 * open copy question for the PO.
	 */
	get signalledNumber(): string | null {
		const number = this.store.outgoingNumber().trim();
		return number.length > 0 ? number : null;
	}

	/**
	 * The typed field is shown when the customer chose it, always when there is nothing to choose
	 * from — otherwise a phone service with no candidates would offer no way in at all — and
	 * whenever the number already settled is one no row can represent.
	 *
	 * That last case is not theoretical: a phone service can arrive with a configured outgoing
	 * number AND a candidate list that does not contain it. Without this, the field stayed hidden,
	 * no row was ticked, the `sip_number_already_stored` line lives inside this block so it was
	 * suppressed too — and the continue button was enabled the whole time. The customer confirmed a
	 * number the step never showed them, on the step whose entire job is to show it.
	 */
	get showCustomInput(): boolean {
		return (
			this.availableNumbers.length === 0 ||
			this.store.numberMode() === 'custom' ||
			this.holdsNumberOutsideTheList
		);
	}

	/** A settled number that matches no candidate row, so only the field can display it. */
	private get holdsNumberOutsideTheList(): boolean {
		const number = this.signalledNumber;
		return number !== null && !this.availableNumbers.some((c) => c.number.trim() === number);
	}

	/**
	 * Back only where something precedes this screen. From the order that is the number step, but
	 * only when there was one — a phone service whose number was already configured never saw it, and
	 * an inert button is worse than none.
	 */
	get canGoBack(): boolean {
		// The link path has no number step to go back to.
		if (this.isLinkEntry) {
			return false;
		}
		// The number step now always precedes the order, so there is always something behind it.
		return this.store.phase() === 'order';
	}

	back(): void {
		if (this.canGoBack) {
			this.store.phase.set('chooseNumber');
		}
	}

	/** The i18n key of a rail step's label. */
	stepLabel(step: SetupStep): I18nSipSetupEnum {
		switch (step) {
			case 'intro':
				return I18nSipSetupEnum.SIP_STEP_INTRO;
			case 'signIn':
				return I18nSipSetupEnum.SIP_STEP_SIGN_IN;
			case 'chooseNumber':
				return I18nSipSetupEnum.SIP_STEP_CHOOSE_NUMBER;
			case 'orderSipAccount':
				return I18nSipSetupEnum.SIP_STEP_ORDER_SIP;
			case 'verifyNumber':
				return I18nSipSetupEnum.SIP_STEP_VERIFY_NUMBER;
			case 'confirmation':
				return I18nSipSetupEnum.SIP_STEP_CONFIRMATION;
		}
	}

	/** The header's progress line: `sip_progress_step` with its placeholders filled. */
	get progressLabel(): string {
		return fillPlaceholders(this.translate(I18nSipSetupEnum.SIP_PROGRESS_STEP), {
			STEP: this.currentStepIndex,
			TOTAL: this.totalSteps - 1,
		});
	}

	/** The header's "signed in as" tooltip: `sip_signed_in_as` with the name filled in. */
	get signedInAsLabel(): string {
		return fillPlaceholders(this.translate(I18nSipSetupEnum.SIP_SIGNED_IN_AS), {
			NAME: this.store.signedInAs(),
		});
	}

	isStepDone(step: SetupStep): boolean {
		switch (step) {
			case 'intro':
				// Left behind the moment the run starts, and it never becomes pending again.
				return this.store.phase() !== 'intro';
			case 'confirmation':
				// Its own screen is the end of the flow, so it is never "done" while it is on show.
				return false;
			case 'signIn':
				return this.isSignedIn;
			case 'chooseNumber': {
				// Settled once the flow has moved PAST it with a number in hand. The list of phases
				// matters: checking only for 'order' made the step un-tick itself again on the result
				// screens, so the rail showed a completed step as pending.
				const past: SetupPhase[] = [
					'order',
					'verify',
					'verifyRunning',
					'verifyResult',
					'verified',
					'booked',
					'rejected',
					'unconfirmed',
					'addressInvalid',
				];
				return past.includes(this.store.phase()) && this.signalledNumber !== null;
			}
			case 'orderSipAccount':
				return this.store.sipOrdered();
			case 'verifyNumber':
				// Passed once the overview is reached, proven or skipped - the step asked, it is behind.
				return this.store.phase() === 'booked';
		}
	}

	isStepCurrent(step: SetupStep): boolean {
		const phase = this.store.phase();
		if (step === 'signIn') {
			return phase === 'signIn';
		}
		if (step === 'intro') {
			return phase === 'intro';
		}
		if (step === 'confirmation') {
			return (
				phase === 'booked' ||
				phase === 'rejected' ||
				phase === 'unconfirmed' ||
				phase === 'addressInvalid'
			);
		}
		if (step === 'chooseNumber') {
			return phase === 'chooseNumber';
		}
		if (step === 'verifyNumber') {
			return phase === 'verify' || phase === 'verifyRunning' || phase === 'verifyResult' || phase === 'verified';
		}
		// The order step ends when the booking answers. Until 2026-09-11 it kept "current" through
		// the result screens, because they were its outcome and nothing else represented them — that
		// was the honest reading while the rail had no confirmation step. It has one now, so the
		// order is done and the confirmation is current.
		return phase === 'order';
	}

	/** Says what is missing, rather than one sentence that is wrong on one of the three runs. */
	get introLead(): I18nSipSetupEnum {
		const requirement = this.requirement;
		if (!requirement.needsOutgoingNumber && !requirement.needsSipAccount) {
			return I18nSipSetupEnum.SIP_INTRO_VERIFY_ONLY;
		}
		if (!requirement.needsOutgoingNumber) {
			return I18nSipSetupEnum.SIP_INTRO_CONNECTION_ONLY;
		}
		return requirement.needsSipAccount
			? I18nSipSetupEnum.SIP_INTRO_NOTHING_CONFIGURED
			: I18nSipSetupEnum.SIP_INTRO_NUMBER_ONLY;
	}

	/** Every run signs in since 2026-10-02 — derived from the steps so there is one source. */
	get requiresSignIn(): boolean {
		return this.requirement.steps.includes('signIn');
	}

	/**
	 * Whether this run BOOKS something. Deliberately separate from `requiresSignIn`: the two used to
	 * coincide, and the intro's promise list was gated on the sign-in because of it. Now that every
	 * run signs in, that gate would promise a SIP-Anschluss and a contract extension on the free
	 * number-only run — which was a High finding in the code review the first time around.
	 */
	get booksSipAccount(): boolean {
		return this.requirement.needsSipAccount;
	}

	/** Every run signs in first (see `deriveSetupRequirement`), so the run starts there. */
	start(): void {
		this.store.active.set(true);
		this.enterSignIn();
	}

	/**
	 * L1 (code review 2): the header's close button is disabled while a sign-in is in flight. The
	 * credentials live on the component (D3 - never in the root store), so closing mid-request would
	 * let the pending answer land on a re-created component with empty fields: a 2FA prompt above a
	 * blank username and password. The wait is bounded by the sign-in and `check_token` timeouts.
	 *
	 * Also disabled while the ORDER is in flight (PR review, MI1). The footer's Cancel is already
	 * disabled there, so the header close was the one exit left, and it left a possibly billed order
	 * nobody had observed: the answer lands in the store, but the contact callback that tells the
	 * service about a refused or unconfirmed order is only created by a click on the screen that
	 * answer renders. The wait is bounded by `SIP_ORDER_TIMEOUT_MS`.
	 */
	get closeDisabled(): boolean {
		const phase = this.store.phase();
		return this.store.busy() && (phase === 'signIn' || phase === 'order');
	}

	/** Closes the dialog and keeps the run: the next press of the call button resumes here. */
	suspend(): void {
		this.logEnd('suspended');
		this.suspended.emit();
	}

	/** Gives up on the whole run. */
	dismiss(): void {
		this.logEnd('dismissed');
		this.dismissed.emit();
	}

	/**
	 * The phases that end in the contact callback. On them the header's close button ends the run
	 * the same way their own close button does: suspending there would keep a run that has nothing
	 * left to do, and drop the follow-up the screen has just promised.
	 *
	 * `rejected` is not one of them (Torsten, 2026-10-16): the customer chooses there whether the
	 * service gets in touch, and closing means no. Nothing was booked - the order rolled back - so
	 * ending the run is safe. `unconfirmed` keeps the automatic follow-up: the booking may exist, and
	 * a fresh run could order a second billed account.
	 *
	 * `addressInvalid` is one of them (developer decision D7): nothing was booked, but the screen
	 * promises that the service checks the address, and a plain close would drop that.
	 *
	 * @ticket PS-6605
	 */
	private static readonly CONTACT_SERVICE_PHASES: readonly SetupPhase[] = [
		'unconfirmed',
		'addressInvalid',
		'serviceFollowUp',
	];

	/** The header's close button: suspends, except on a screen that ends the run. */
	closeFromHeader(): void {
		if (this.closeDisabled) {
			return;
		}
		if (GuidedSetup.CONTACT_SERVICE_PHASES.includes(this.store.phase())) {
			this.endWithServiceContact();
			return;
		}
		if (this.store.phase() === 'rejected') {
			this.dismiss();
			return;
		}
		this.suspend();
	}

	/** Ends the run with the contact callback - see the `contactService` output. */
	endWithServiceContact(): void {
		this.logEnd('contactService');
		this.contactService.emit();
	}

	/**
	 * The SPECIAL CASE (`isNumberSetWithoutSipAccount`): no SIP-Account, but an outgoing number is
	 * already configured. Such a run is not offered the contact path after a sign-in problem
	 * (developer decision, 2026-10-12).
	 */
	get isNumberSetWithoutSipAccount(): boolean {
		return isNumberSetWithoutSipAccount(this.signals);
	}

	/**
	 * Whether the secondary `sip_btn_contact_service` button is on screen: after a refused or
	 * technically failed sign-in, and on the failure screen - never in the special case. Not after
	 * an expired token or a wrong second factor: both are fixed on the same step.
	 */
	get offersContactService(): boolean {
		// The link page has no callnote, so there is no callback to create.
		if (this.isLinkEntry || this.isNumberSetWithoutSipAccount) {
			return false;
		}
		const phase = this.store.phase();
		if (phase === 'failed') {
			return true;
		}
		const error = this.store.loginError();
		return (
			phase === 'signIn' &&
			!this.store.busy() &&
			(error === I18nSipSetupEnum.SIP_LOGIN_REJECTED ||
				error === I18nSipSetupEnum.SIP_LOGIN_UNAVAILABLE ||
				// PS-6646: someone without the right account's credentials cannot fix this on the step.
				error === I18nSipSetupEnum.SIP_LOGIN_WRONG_ACCOUNT)
		);
	}

	/**
	 * The customer asked the service to contact them. Shows what happens next first; the callback is
	 * created when they close that screen. A failure's diagnostic is logged here, because this exit
	 * bypasses `acknowledgeFailure()` and with it the parent's `onSetupFailed`.
	 */
	requestServiceContact(): void {
		if (!this.offersContactService) {
			return;
		}
		if (this.store.phase() === 'failed') {
			this.logger.error(LOG_EVENTS.failed, {
				stage: 'run',
				reason: this.store.failureReason() ?? 'unknown',
				phoneServiceId: this.store.phoneServiceId(),
			});
		}
		this.password = '';
		this.twoFactorCode = '';
		this.store.phase.set('serviceFollowUp');
	}

	/**
	 * A run that ended without a booking, and the step it ended ON - which is the whole value of the
	 * event. "Customers leave" is not actionable; "customers leave on the order screen" is.
	 *
	 * Suspending and dismissing are kept apart because they are not the same decision: one keeps the
	 * run and comes back, the other does not.
	 */
	private logEnd(outcome: 'suspended' | 'dismissed' | 'contactService'): void {
		this.logger.usage(LOG_EVENTS.ended, {
			outcome,
			step: this.store.phase(),
			setupCase: this.requirement.setupCase,
			booked: this.orderInFlight ? 'pending' : this.store.sipOrdered(),
			phoneServiceId: this.store.phoneServiceId(),
		});
	}

	/**
	 * An order request is out and has not answered. Suspending now must not log `booked: false` for
	 * a booking that may still succeed (L1) - its answer still lands in the store, see `order()`.
	 */
	private get orderInFlight(): boolean {
		return this.store.busy() && this.store.phase() === 'order';
	}

	/** Enters the sign-in step with an empty form; a resumed run never carries credentials. */
	private enterSignIn(): void {
		this.store.phase.set('signIn');
		this.resetLoginForm();
	}

	/**
	 * The credentials live on the component, never in the store. The store is root-provided and
	 * outlives the dialog on purpose - cancelling has to reopen at the same step - which is exactly
	 * where a password must not be. So closing the dialog takes the typed password with it.
	 */
	private resetLoginForm(): void {
		// TEMPORARY, mock mode only: pre-filled so a reviewer walking the flow for the fifth time
		// does not retype credentials the mock does not read anyway. The step still renders and
		// still has to be submitted - skipping it would hide a screen that is under review. Dead on
		// live twice over (see mock-mode.ts), and it goes with the harness.
		const prefill = isMockMode();
		this.username = prefill ? MOCK_LOGIN.username : '';
		this.password = prefill ? MOCK_LOGIN.password : '';
		this.twoFactorCode = '';
		this.store.signedInAs.set('');
		this.store.twoFactorRequired.set(false);
		this.store.loginError.set(null);
	}

	/**
	 * A getter, not a `computed()`. `username` and `password` are plain properties, so a computed
	 * would cache its first answer and the button would never enable - the same trap that already
	 * cost us once on `setupRequirement`.
	 */
	get canSignIn(): boolean {
		if (this.store.busy()) {
			return false;
		}
		if (this.store.twoFactorRequired() && !this.twoFactorCode.trim()) {
			return false;
		}
		return this.username.trim().length > 0 && this.password.length > 0;
	}

	/**
	 * The sign-in form's submit (L11): Enter in a field, or the footer button bound to the form.
	 * The native submission is cancelled - this page must not navigate - and the button's own
	 * condition still applies, because implicit submission does not go through the button.
	 */
	submitSignIn(event: Event): void {
		event.preventDefault();
		if (this.canSignIn) {
			void this.signIn();
		}
	}

	/**
	 * Two of the four outcomes keep the customer on this step rather than ending the run. That is
	 * the point of the inline form: a wrong password or a missing second factor is something to
	 * correct here, not a reason to lose them — which is what the popup did.
	 */
	async signIn(): Promise<void> {
		this.store.loginError.set(null);
		this.store.busy.set(true);
		const outcome = await this.inlineLogin.signIn({
			username: this.username,
			password: this.password,
			twoFactorCode: this.twoFactorCode || undefined,
		});
		this.store.busy.set(false);

		if (outcome.result === 'twoFactorRequired') {
			// Not an error. The credentials were accepted and a code is now needed, so the field
			// appears and the same credentials go out again with it.
			// A code was already supplied and the server still wants one, so that code was wrong.
			// Which `reason` the proxy sends for a wrong code is UNVERIFIED — nobody has triggered it
			// — so this infers it from the fact that we had already sent one.
			const codeWasRejected = this.twoFactorCode.length > 0;
			this.store.twoFactorRequired.set(true);
			this.store.loginError.set(codeWasRejected ? I18nSipSetupEnum.SIP_LOGIN_TWO_FACTOR_WRONG : null);
			this.twoFactorCode = '';
			return;
		}
		if (outcome.result === 'rejected') {
			this.store.loginError.set(I18nSipSetupEnum.SIP_LOGIN_REJECTED);
			this.password = '';
			return;
		}
		if (outcome.result === 'failed') {
			/*
			 * A TECHNICAL failure (5xx or network without the proxy's body, timeout, unparsable
			 * response) keeps the customer on the sign-in step with its own copy (M6, developer
			 * decision 2026-10-12): `sip_login_unavailable` instead of telling them to check
			 * credentials that may well be right. The contact path is offered beside it.
			 *
			 * Known consequence, recorded so it is not rediscovered: until the proxy answers 401 for a
			 * refused login, a mistyped password on the Connect hosts also arrives as an unparsable 200
			 * (the proxy's 500 laundered by CloudFront's custom_error_responses; measured on live
			 * 2026-09-11, PS-6519) and therefore shows this copy too.
			 *
			 * The technical reason goes to the CONSOLE, deliberately not to `LoggingService` (L17):
			 * the log bridge requires a bearer and a failed sign-in has none, so the event could
			 * never be sent.
			 */
			console.error('PS-6495 sign-in failed:', outcome.reason);
			this.store.loginError.set(I18nSipSetupEnum.SIP_LOGIN_UNAVAILABLE);
			this.password = '';
			return;
		}

		// The password has done its job. The username stays, because the following steps say who is
		// signed in - a rail dot turning into a tick is easy to miss, and the customer has just
		// handed over credentials on a page they were told not to.
		this.store.signedInAs.set(this.username);
		this.password = '';
		this.twoFactorCode = '';
		this.store.twoFactorRequired.set(false);

		await this.resolvePhoneService();

		// AFTER the phone service is resolved, not before: until then there is no id to tie the event
		// to, and a sign-in whose token resolves nothing is not a sign-in this flow can use. The case
		// travels with it, plus the two facts it is derived from - the number is this app's reading of
		// the matrix and could drift, the booleans are what the payload actually said.
		if (this.store.phoneServiceId() !== null) {
			const requirement = this.requirement;
			this.logger.usage(LOG_EVENTS.signedIn, {
				setupCase: requirement.setupCase,
				needsOrder: requirement.needsSipAccount,
				needsNumber: requirement.needsOutgoingNumber,
				phoneServiceId: this.store.phoneServiceId(),
			});
		}
	}

	/**
	 * The booking needs a phone service the token vouches for, and there is no second source to fall
	 * back on — so anything other than exactly one is refused rather than guessed.
	 */
	private async resolvePhoneService(): Promise<void> {
		const token = this.tokenStore.token;
		if (!token) {
			this.fail('authorized without a token in the store');
			return;
		}

		// Busy across BOTH requests - the introspection and the legal-links fetch below - so the
		// customer is never left on the sign-in screen with a cleared password and nothing saying why
		// (PR review, MA2).
		this.store.busy.set(true);
		const resolution = await this.introspection.resolvePhoneServiceId(token);

		if (resolution.result !== 'resolved') {
			this.store.busy.set(false);
			// Each variant contributes what it actually knows. `failed` carries a `reason` that exists
			// for exactly this and was previously dropped, so the log said "failed" where it could
			// have said which call broke and with what status.
			const detail =
				resolution.result === 'ambiguous'
					? `token vouches for ${resolution.count} phone services`
					: resolution.result === 'failed'
						? resolution.reason
						: resolution.result;
			this.fail(`phone service not resolvable: ${detail}`);
			return;
		}

		/**
		 * PS-6646: everything this run does - the order, the number, its verification - acts on the
		 * phone service set below, so it must be the callnote's. Checked before it is set, before the
		 * legal links, before any resume. Without the callnote's id there is nothing to check against,
		 * and proceeding on the token alone is exactly the bug, so the run stops.
		 */
		const expected = this.expectedPhoneServiceId;
		if (expected === null) {
			this.store.busy.set(false);
			this.fail('callnote phone service unknown');
			return;
		}
		if (resolution.phoneServiceId !== expected) {
			this.store.busy.set(false);
			this.refuseWrongAccount(expected, resolution.phoneServiceId);
			return;
		}

		this.store.phoneServiceId.set(resolution.phoneServiceId);

		// The brand's legal URLs, fetched here because this is the first moment both things they need
		// exist: a bearer token, and the brand that came back on the same introspection. Awaited so
		// the order screen never renders mid-fetch with half its links; it never rejects, and an
		// empty result degrades to unlinked document names rather than blocking the run.
		try {
			await this.legalLinks.load(resolution.brandId);
		} finally {
			this.store.busy.set(false);
		}

		// Coming back from an expired sign-in: the number and the consent are still settled, so the
		// customer returns to the screen they were taken off rather than walking the flow again. A
		// different account can no longer get here - it is refused above (PS-6646, replaces L3).
		const resume = this.store.resumeAfter();
		if (resume !== null) {
			this.store.resumeAfter.set(null);
			this.store.phase.set(resume);
			return;
		}
		// The number comes first, so the order screen can state what it will actually signal — and
		// it comes first even when one is already configured, so it is confirmed rather than assumed.
		this.store.phase.set('chooseNumber');
	}

	/**
	 * PS-6646 — the account signed in does not own the callnote's phone service. A correctable
	 * mistake, so the customer stays on the sign-in step and may try again; `resumeAfter` is kept, so
	 * the right account still returns to where the run was.
	 *
	 * Logged FIRST: the log bridge needs a bearer, and the refused token is the only one there is.
	 */
	private refuseWrongAccount(expectedPhoneServiceId: number, signedInPhoneServiceId: number): void {
		this.logger.usage(LOG_EVENTS.signInWrongAccount, { expectedPhoneServiceId, signedInPhoneServiceId });
		this.tokenStore.clear();
		this.store.signedInAs.set('');
		this.store.loginError.set(I18nSipSetupEnum.SIP_LOGIN_WRONG_ACCOUNT);
	}

	// ── Consent ───────────────────────────────────────────────────────────────────────

	toggleDeclaration(): void {
		this.store.declarationAccepted.set(!this.store.declarationAccepted());
		this.store.consentError.set(null);
	}

	/**
	 * The § 120 TKG declaration as displayed. Sent verbatim on the trigger, so what the customer saw
	 * is what is stored as the evidence. Like the two order consents it is identical German in every
	 * language row.
	 */
	get declarationText(): string {
		return this.translate(I18nSipSetupEnum.SIP_CONSENT_DECLARATION_TKG120);
	}

	/**
	 * The declaration sits on the number step, beside the number it is about, whenever a
	 * verification follows (Torsten, 2026-10-14). The order screen keeps only the two contract
	 * consents.
	 */
	get numberAsksDeclaration(): boolean {
		return this.offersVerification;
	}

	get declarationMissing(): boolean {
		return this.store.declarationAttempted() && !this.store.declarationAccepted();
	}

	toggleTerms(): void {
		this.store.termsAccepted.set(!this.store.termsAccepted());
		this.store.consentError.set(null);
	}

	/**
	 * The § 71 Abs. 3 TKG declaration. Clearing the error on every toggle is the same courtesy the
	 * terms box gets: once the customer has acted on what the message asked for, leaving it on
	 * screen tells them off for something they have just fixed.
	 */
	toggleWaiver(): void {
		this.store.waiverAccepted.set(!this.store.waiverAccepted());
		this.store.consentError.set(null);
	}

	/**
	 * The two consent sentences, translated, with their links resolved for this brand.
	 *
	 * Both are BINDING GERMAN LEGAL TEXT and carry the identical German in every language row on
	 * purpose (see `PS-6495-i18n-inserts.sql`): a § 71 Abs. 3 TKG waiver is a declaration under
	 * German law, and a rendering in another language would be a different statute. The contracting
	 * party they name is eGroup Technologies AG, not the brand the customer arrived through.
	 *
	 * The rows mark each link as `{egroup_agb}label{/egroup_agb}`; the marker names the key the
	 * brand's URL is stored under (global key `url`, via `LegalLinksService`), and
	 * `renderLegalLinks` turns the pair into an anchor - or leaves the label as plain text when the
	 * brand has no row.
	 */
	get agbText(): string {
		return renderLegalLinks(this.translate(I18nSipSetupEnum.SIP_CONSENT_AGB), this.legalLinks.current());
	}

	/** M15: the provider's homepage for this brand, or null - then the name renders as plain text. */
	get providerUrl(): string | null {
		return this.legalLinks.current().egroup_homepage ?? null;
	}

	get waiverText(): string {
		return renderLegalLinks(
			this.translate(I18nSipSetupEnum.SIP_CONSENT_WAIVER_TKG71),
			this.legalLinks.current(),
		);
	}

	/**
	 * Fail closed on the legal text. A missing key resolves to an EMPTY string, and on this screen
	 * that would be a contract concluded with no visible terms - so without both sentences the
	 * order cannot be confirmed at all.
	 */
	get consentTextAvailable(): boolean {
		return (
			this.translate(I18nSipSetupEnum.SIP_CONSENT_AGB).trim().length > 0 &&
			this.translate(I18nSipSetupEnum.SIP_CONSENT_WAIVER_TKG71).trim().length > 0
		);
	}

	/**
	 * Which required field is missing — but only after the customer has pressed the button. Marking
	 * fields red before anyone has done anything reads as an accusation on a screen they have only
	 * just reached.
	 */
	get termsMissing(): boolean {
		return this.store.consentAttempted() && !this.store.termsAccepted();
	}

	get waiverMissing(): boolean {
		return this.store.consentAttempted() && !this.store.waiverAccepted();
	}

	/**
	 * The button stays enabled and the gaps light up on press (Torsten, 2026-10-07) — the pattern
	 * the rest of this flow already uses. A disabled button with no explanation leaves the customer
	 * guessing which of two controls it is waiting for.
	 */
	consentIsComplete(): boolean {
		this.store.consentAttempted.set(true);
		const termsMissing = !this.store.termsAccepted();
		const waiverMissing = !this.store.waiverAccepted();

		if (termsMissing && waiverMissing) {
			this.store.consentError.set(I18nSipSetupEnum.SIP_ERROR_CONSENT_BOTH);
			return false;
		}
		if (termsMissing) {
			this.store.consentError.set(I18nSipSetupEnum.SIP_ERROR_CONSENT_TERMS);
			return false;
		}
		if (waiverMissing) {
			this.store.consentError.set(I18nSipSetupEnum.SIP_ERROR_CONSENT_WAIVER);
			return false;
		}

		this.store.consentError.set(null);
		return true;
	}

	// ── Order ────────────────────────────────────────────────────────────────────────────────────

	/**
	 * Deliberately NOT gated on the consent boxes. A disabled button with no explanation leaves the
	 * customer guessing which of two long legal paragraphs they missed; pressing it and being told
	 * exactly what is missing, in the prototype's own words, is the better failure.
	 */
	get canOrder(): boolean {
		return !this.store.busy() && this.orderTarget() !== null && this.consentTextAvailable;
	}

	/** Where the order goes, decided by the entry and never by a token's content; null disables confirm. */
	orderTarget(): SipOrderTarget | null {
		if (this.isLinkEntry) {
			const token = this.sipOrderLink.token;
			return token === null ? null : { kind: 'link', token };
		}
		const phoneServiceId = this.store.phoneServiceId();
		return phoneServiceId === null ? null : { kind: 'phoneService', phoneServiceId };
	}

	order(): void {
		const target = this.orderTarget();
		if (!this.canOrder || target === null) {
			return;
		}
		if (!this.consentIsComplete()) {
			return;
		}
		if (target.kind === 'link') {
			this.orderViaLink(target);
			return;
		}

		// A token already known to be gone buys nothing by being spent: the request would 401 and the
		// customer would watch a failure that was predictable. The margin is a hint, never the
		// authority — the 401 branch below still has to exist, because clocks drift and the server
		// can revoke early.
		if (this.tokenStore.isExpired) {
			this.requireNewSignIn();
			return;
		}

		this.store.busy.set(true);
		/**
		 * No `takeUntilDestroyed`, on purpose (L18, code review 2). The dialog's own buttons cannot close
		 * it while the order is out (`closeDisabled`), but nothing else guarantees this component
		 * outlives the request - and the booking's answer must still land: a billed order that went
		 * through has to show as booked when the run is resumed, and a refusal has to reach the contact
		 * path. Every write below goes to the root-provided store,
		 * so the late answer is safe. The request completes or times out (`SIP_ORDER_TIMEOUT_MS`),
		 * so the subscription cannot outlive it.
		 */
		this.orderService.orderSipAccount$(target, new Date()).subscribe({
			next: (response) => {
				this.store.busy.set(false);

				/*
				 * Confirmed or refused is decided by whether a NUMBER came back — and that is now the
				 * sourced answer, not a guess.
				 *
				 * `result` only ever holds ORDERED or ALREADY_EXISTS (bookingmgmt
				 * `OutboxOrderResponse`, read 2026-09-04), and its Javadoc says why: it distinguishes a
				 * fresh booking from an idempotent hit. Both are successes, both come back through the
				 * same `toResponse(...)` with a number filled in, and there is no rejection value —
				 * a refusal throws in `OutboxOrderService` and arrives here as an HTTP error.
				 *
				 * Branching on `result === 'ORDERED'` would therefore have shown the apology screen to
				 * a customer whose SIP-Account already existed. The number is the honest criterion.
				 */
				const bookedNumber = response?.number?.trim() ?? '';
				/* A success always carries the number, so its absence is an anomaly, not a refusal: the
				 * booking may exist. Unknown outcome, the service checks it. */
				if (!bookedNumber) {
					/**
					 * `result` is the only thing that says what the backend actually did, and the
					 * screen deliberately does not show it to the customer - so it goes to Graylog.
					 */
					this.logger.error(LOG_EVENTS.failed, {
						stage: 'order',
						reason: 'order returned no number',
						result: response?.result ?? null,
						phoneServiceId: this.store.phoneServiceId(),
					});
					this.store.phase.set('unconfirmed');
					return;
				}

				this.store.bookedNumber.set(bookedNumber);
				this.store.sipOrdered.set(true);
				// `ALREADY_EXISTS` is a success too - the account was there and the order was idempotent.
				// Worth keeping apart in Graylog: a rising share of it means something upstream is
				// offering the order to customers who already hold the product.
				this.logger.usage(LOG_EVENTS.ordered, {
					result: response?.result ?? null,
					phoneServiceId: this.store.phoneServiceId(),
				});
				// The PO's order (2026-10-14): book, then prove the number, then the overview the call
				// is started from. Without a verification to run the overview follows directly.
				this.store.phase.set(this.needsVerificationCall ? 'verify' : 'booked');
			},
			error: (error: unknown) => {
				this.store.busy.set(false);
				// An expired sign-in is NOT a refused booking. It used to land here, on a screen that
				// tells the customer to call our service — for something a second sign-in fixes.
				if (error instanceof HttpErrorResponse && error.status === 401) {
					this.requireNewSignIn();
					return;
				}
				/*
				 * Unknown is the default: only a definite refusal (a 4xx, or a 500 from the service
				 * itself) proves nothing was booked. A timeout (SIP_ORDER_TIMEOUT_MS), a dropped
				 * connection (0), the gateway errors (502, 503, 504), a 200 whose body could not be read
				 * (an LB or WAF page) and any non-HTTP error say nothing about the booking: it may have
				 * completed upstream. Telling the customer it failed invites a second, billed order - so
				 * the outcome is "unconfirmed", the service checks it, and there is no retry button.
				 * A refusal bookingmgmt can prove (nothing booked, extension rolled back) is answered with
				 * a 4xx and an `errorCode`; an unusable contract address is 422 ADDRESS_INVALID and has
				 * its own screen below. A 502 stays unknown: it is not a proven refusal, and bookingmgmt
				 * also answers with 502 when a refused order could not be rolled back.
				 */
				const timedOut = error instanceof TimeoutError;
				const status = error instanceof HttpErrorResponse ? error.status : null;
				if (!isDefiniteOrderRefusal(status)) {
					this.logger.error(LOG_EVENTS.failed, {
						stage: 'order',
						reason: `order outcome unknown: ${describeOrderError(error, timedOut, status)}`,
						phoneServiceId: this.store.phoneServiceId(),
					});
					this.store.phase.set('unconfirmed');
					return;
				}
				/* The contract address was unusable: nothing was booked, and the service follows up on it. */
				if (isAddressInvalidRefusal(error)) {
					this.logger.error(LOG_EVENTS.failed, {
						stage: 'order',
						reason: 'order refused: address invalid',
						phoneServiceId: this.store.phoneServiceId(),
					});
					this.store.phase.set('addressInvalid');
					return;
				}
				/* Refused outright: nothing was booked, the customer chooses whether the service gets in touch. */
				this.logger.error(LOG_EVENTS.failed, {
					stage: 'order',
					reason: `order refused: HTTP ${status}`,
					phoneServiceId: this.store.phoneServiceId(),
				});
				this.store.phase.set('rejected');
			},
		});
	}

	/** Link order (D7): a number back is `booked`, everything else `failed`; only the HTTP status is logged. */
	private orderViaLink(target: SipOrderTarget): void {
		this.store.busy.set(true);
		this.orderService.orderSipAccount$(target, new Date()).subscribe({
			next: (response) => {
				this.store.busy.set(false);
				this.sipOrderLink.clear();
				const bookedNumber = response?.number?.trim() ?? '';
				if (!bookedNumber) {
					console.error('PS-6586 link order answered without a number');
					this.store.phase.set('failed');
					return;
				}
				this.store.bookedNumber.set(bookedNumber);
				this.store.sipOrdered.set(true);
				this.store.phase.set('booked');
			},
			error: (error: unknown) => {
				this.store.busy.set(false);
				this.sipOrderLink.clear();
				const status =
					error instanceof HttpErrorResponse
						? `HTTP ${error.status}`
						: error instanceof TimeoutError
							? 'timeout'
							: 'error';
				console.error('PS-6586 link order failed:', status);
				this.store.phase.set('failed');
			},
		});
	}

	// ── Number ───────────────────────────────────────────────────────────────────────────────────

	/** Picks one of the customer's own numbers. */
	pickNumber(value: string): void {
		this.store.numberError.set(null);
		this.store.verificationSkipped.set(false);
		this.store.numberVerified.set(false);
		this.store.numberMode.set('list');
		this.store.outgoingNumber.set(value.trim());
	}

	isNumberPicked(value: string): boolean {
		return this.store.numberMode() === 'list' && this.store.outgoingNumber() === value.trim();
	}

	/**
	 * Switches to typing a different number. Clears the field rather than leaving a picked candidate
	 * sitting in it, so the customer is not silently submitting the number they just moved away from.
	 *
	 * The no-op condition is `showCustomInput`, NOT the mode — deliberately the same expression the
	 * row's `.picked` and `aria-pressed` are bound to. When those two drift apart the row says
	 * "selected" and then destroys something on click: with a configured number that matches no
	 * candidate, the field is already open holding it while the mode is still 'list', so the
	 * mode-only check fell through and blanked a number the backend has on file, greying out
	 * the continue button with no message. Bound to one expression, a pressed row is simply inert;
	 * the customer edits the field, which is right there and already focused-able.
	 */
	useCustomNumber(): void {
		if (this.showCustomInput) {
			return;
		}
		this.store.numberError.set(null);
		this.store.numberVerified.set(false);
		this.store.numberMode.set('custom');
		this.store.outgoingNumber.set('');
	}

	onNumberInput(value: string): void {
		this.store.numberError.set(null);
		this.store.verificationSkipped.set(false);
		this.store.numberVerified.set(false);
		this.store.numberMode.set('custom');
		this.store.outgoingNumber.set(value);
	}

	/**
	 * The format check applies to TYPED input only. A candidate the backend offered is accepted as
	 * delivered — that list is the backend's (D8), and it really does contain short internal numbers:
	 * the recorded live response carries `+8556`, which this check would otherwise reject. Validating
	 * a number the system itself proposed would block customers on the backend's own data.
	 */
	get canFinish(): boolean {
		if (this.store.busy()) {
			return false;
		}
		const value = this.store.outgoingNumber().trim();
		if (this.store.numberMode() === 'list' || this.numberIsPreconfigured) {
			return value.length > 0;
		}
		return isPlausiblePhoneNumber(value);
	}

	/**
	 * Shown only once the customer has TYPED something that cannot be a number — never while the
	 * field is still empty, which would scold them for not having started, and never for a picked
	 * candidate, which is not theirs to get wrong.
	 */
	/**
	 * The format check applies to what the customer TYPES, never to what was already configured.
	 *
	 * Without that exception the step could trap them: a configured number the validator rejects
	 * (short internal numbers are real here — the recorded payload carries `+8556`) is accepted
	 * while untouched, because the untouched value is never validated. One keystroke flips the mode
	 * to 'custom' for good, and the panel then shows `sip_number_already_stored` directly above
	 * `sip_number_invalid` about the same string, with the continue button disabled and —
	 * on the common empty-candidate-list run — no row left to click that would restore the mode. The
	 * only way out was abandoning a number the backend already has on file.
	 */
	get numberFormatError(): I18nSipSetupEnum | null {
		if (this.store.numberMode() === 'list' || this.numberIsPreconfigured) {
			return null;
		}
		const value = this.store.outgoingNumber().trim();
		if (value.length === 0 || isPlausiblePhoneNumber(value)) {
			return null;
		}
		return I18nSipSetupEnum.SIP_NUMBER_INVALID;
	}

	/** The format error as shown: only once the field has been left (L9). */
	get visibleNumberError(): I18nSipSetupEnum | null {
		return this.numberTouched ? this.numberFormatError : null;
	}

	/** True when an order screen follows, so the number step continues rather than concludes. */
	get numberStepContinues(): boolean {
		return this.requirement.needsSipAccount;
	}

	/**
	 * Leaves the number step. With an order still to come this is a step forward; without one the
	 * number was the only thing missing, so settling it completes the run.
	 */
	confirmNumber(): void {
		if (!this.canFinish) {
			return;
		}
		// WHERE it came from, never WHICH it is (Torsten, 2026-10-09). A phone number is personal data
		// and this is a public page; the funnel only needs to know the step was passed and whether the
		// customer had a row to pick or had to type.
		this.logger.usage(LOG_EVENTS.numberSet, {
			source: this.store.numberMode() === 'list' ? 'picked' : 'typed',
			setupCase: this.requirement.setupCase,
			phoneServiceId: this.store.phoneServiceId(),
		});
		if (!this.offersVerification) {
			this.leaveNumberStep();
			return;
		}
		const value = this.store.outgoingNumber().trim();
		const precheck = precheckPhoneNumber(value);
		if (precheck !== null) {
			this.store.numberError.set(precheck);
			return;
		}
		// Fail closed on the legal text, like the order consents: no visible sentence, no declaration.
		if (!this.store.declarationAccepted() || this.declarationText.trim().length === 0) {
			this.store.declarationAttempted.set(true);
			return;
		}
		this.validateNumber(value);
	}

	/** Whether this run ends with proving the number by a call. */
	get offersVerification(): boolean {
		return this.requirement.offersVerification;
	}

	/** Verification belongs to the run and the number is not already proven. */
	private get needsVerificationCall(): boolean {
		return this.offersVerification && !this.store.numberVerified() && !this.store.verificationSkipped();
	}

	/**
	 * A refused number never ends the run (AC7, D19). Where a verification would follow, the customer
	 * may go on without it; the SIP number is signalled, as it was for this case before verification
	 * existed. A number that is not a number at all (format) is not offered this way out.
	 */
	get numberStepCanSkipVerification(): boolean {
		const error = this.store.numberError();
		return (
			this.offersVerification &&
			!this.store.busy() &&
			error !== null &&
			error !== I18nSipSetupEnum.SIP_NUMBER_INVALID &&
			this.store.outgoingNumber().trim().length > 0
		);
	}

	/** Leaves the number step without proving the number; the SIP number is signalled (D19). */
	continueWithoutVerification(): void {
		if (!this.numberStepCanSkipVerification) {
			return;
		}
		this.logger.usage(LOG_EVENTS.verificationFinished, {
			status: null,
			family: 'skipped',
			phoneServiceId: this.store.phoneServiceId(),
		});
		this.store.numberError.set(null);
		this.store.verificationSkipped.set(true);
		this.leaveNumberStep();
	}

	/**
	 * phoneservice's verdict on the number before anything is booked against it. A technical failure
	 * does not block the run (fail-open, decided 2026-10-14): the pre-check passed, and the trigger
	 * re-validates on the server before any call is placed.
	 */
	private validateNumber(value: string): void {
		const phoneServiceId = this.store.phoneServiceId();
		if (phoneServiceId === null) {
			this.leaveNumberStep();
			return;
		}
		this.store.busy.set(true);
		// Bound to the dialog: a late verdict must not move a run that was closed.
		this.cliVerification
			.validate$(phoneServiceId, normalizePhoneNumber(value))
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				finalize(() => this.store.busy.set(false)),
			)
			.subscribe({
				next: (response) => {
					this.store.busy.set(false);
					this.logger.usage(LOG_EVENTS.numberValidated, { outcome: response.outcome, phoneServiceId });
					const verdict = numberStepVerdict(response.outcome);
					if (!verdict.pass) {
						this.store.numberError.set(verdict.error);
						return;
					}
					this.store.numberVerified.set(verdict.alreadyVerified);
					this.leaveNumberStep();
				},
				error: (error: unknown) => {
					this.store.busy.set(false);
					if (error instanceof HttpErrorResponse && error.status === 401) {
						this.requireNewSignIn();
						return;
					}
					const reason = error instanceof HttpErrorResponse ? `HTTP ${error.status}` : 'timeout or network';
					/*
					 * Fail open only where asking again might work; the trigger re-validates anyway. A refusal
					 * (400, 403, 404) is a broken contract or a permission problem, not a blip.
					 */
					if (!(error instanceof HttpErrorResponse) && !(error instanceof TimeoutError)) {
						// Not a network answer at all: a defect on this page. Still fail open, but say so.
						this.logger.error(LOG_EVENTS.failed, {
							stage: 'number',
							reason: 'validate failed: ' + (error instanceof Error ? error.name : 'unknown'),
							phoneServiceId,
						});
					}
					if (!isTransientError(error)) {
						this.logger.error(LOG_EVENTS.failed, { stage: 'number', reason: 'validate refused: ' + reason, phoneServiceId });
						this.store.numberError.set(I18nSipSetupEnum.SIP_VERIFY_FAILED_LEAD);
						return;
					}
					this.logger.usage(LOG_EVENTS.numberValidated, { outcome: 'unavailable', phoneServiceId });
					this.leaveNumberStep();
				},
			});
	}

	/**
	 * The order comes next if one is due, then the verification. A run that offers verification
	 * always ends on the overview, also when the number turned out to be proven already (D16): the
	 * customer starts the call there, as in every other case, and the rail's last step is not skipped.
	 * Only a run without verification completes here.
	 */
	private leaveNumberStep(): void {
		if (this.numberStepContinues) {
			this.store.phase.set('order');
			return;
		}
		if (this.offersVerification) {
			this.store.phase.set(this.needsVerificationCall ? 'verify' : 'booked');
			return;
		}
		this.finish();
	}

	// ── Verification ─────────────────────────────────────────────────────────────────────────────

	/** The trigger button is replaced by its busy label while the request is out (AC5). */
	get verifyTriggerBusy(): boolean {
		return this.store.busy() && this.store.phase() === 'verify';
	}

	triggerVerification(): void {
		const phoneServiceId = this.store.phoneServiceId();
		const number = this.signalledNumber;
		if (phoneServiceId === null || number === null || this.store.busy()) {
			return;
		}
		if (!this.store.declarationAccepted()) {
			this.store.declarationAttempted.set(true);
			this.store.phase.set('chooseNumber');
			return;
		}
		if (this.tokenStore.isExpired) {
			this.requireNewSignIn();
			return;
		}
		this.verification.trigger(phoneServiceId, normalizePhoneNumber(number), this.declarationText);
	}

	cancelVerification(): void {
		const phoneServiceId = this.store.phoneServiceId();
		if (phoneServiceId !== null) {
			this.verification.cancel(phoneServiceId);
		}
	}

	/** Back to the number step; a running call is cancelled first so its code dies with it. */
	editNumber(): void {
		// A changed number needs the declaration again: it is about the number, not the customer.
		const back = (): void => {
			this.store.numberVerified.set(false);
			this.store.verificationResult.set(null);
			this.store.declarationAccepted.set(false);
			this.store.declarationAttempted.set(false);
			this.store.phase.set('chooseNumber');
		};
		const phoneServiceId = this.store.phoneServiceId();
		if (this.store.phase() === 'verifyRunning' && phoneServiceId !== null) {
			this.verification.cancel(phoneServiceId, back);
			return;
		}
		back();
	}

	retryVerification(): void {
		this.store.verificationResult.set(null);
		this.store.phase.set('verify');
	}

	/** Never a dead end (AC7): the overview and the callback stay one click away, signalling 032. */
	skipVerification(): void {
		const phoneServiceId = this.store.phoneServiceId();
		if (this.store.phase() === 'verifyRunning' && phoneServiceId !== null) {
			this.verification.cancel(phoneServiceId, () => this.store.phase.set('booked'));
			return;
		}
		this.verification.stop();
		this.store.verificationResult.set(null);
		this.store.phase.set('booked');
	}

	requestManualVerification(): void {
		const phoneServiceId = this.store.phoneServiceId();
		const number = this.signalledNumber;
		if (phoneServiceId !== null && number !== null) {
			this.verification.requestManual(phoneServiceId, normalizePhoneNumber(number), this.manualNote);
		}
	}

	/** Whether the result screen may offer to try again right now. */
	get verifyResultCanRetry(): boolean {
		const family = this.store.verificationResult()?.family;
		return family === 'wrongCode' || family === 'notReached' || family === 'ourSide';
	}

	get verifyResultCanEditNumber(): boolean {
		const result = this.store.verificationResult();
		if (result?.numberChangeable === false) {
			return false;
		}
		const family = result?.family;
		return family === 'notPossible' || family === 'wrongCode' || family === 'notReached';
	}

	get verifyResultIsLocked(): boolean {
		return this.store.verificationResult()?.family === 'locked';
	}

	/** Title and lead of the result screen, by family. */
	get verifyResultCopy(): { title: I18nSipSetupEnum; lead: I18nSipSetupEnum } {
		switch (this.store.verificationResult()?.family) {
			case 'wrongCode':
				return {
					title: I18nSipSetupEnum.SIP_VERIFY_WRONG_CODE_TITLE,
					lead: I18nSipSetupEnum.SIP_VERIFY_WRONG_CODE_LEAD,
				};
			case 'notReached':
				return {
					title: I18nSipSetupEnum.SIP_VERIFY_NOT_REACHED_TITLE,
					lead: I18nSipSetupEnum.SIP_VERIFY_NOT_REACHED_LEAD,
				};
			case 'locked':
				return { title: I18nSipSetupEnum.SIP_VERIFY_LOCKED_TITLE, lead: I18nSipSetupEnum.SIP_VERIFY_LOCKED_LEAD };
			case 'rateLimited':
				return {
					title: I18nSipSetupEnum.SIP_VERIFY_RATE_LIMITED_TITLE,
					lead: I18nSipSetupEnum.SIP_VERIFY_RATE_LIMITED_LEAD,
				};
			case 'inFlight':
				return {
					title: I18nSipSetupEnum.SIP_VERIFY_IN_FLIGHT_TITLE,
					lead: I18nSipSetupEnum.SIP_VERIFY_IN_FLIGHT_LEAD,
				};
			case 'notPossible':
				return {
					title: I18nSipSetupEnum.SIP_VERIFY_NOT_POSSIBLE_TITLE,
					lead: I18nSipSetupEnum.SIP_VERIFY_NOT_POSSIBLE_LEAD,
				};
			default:
				return { title: I18nSipSetupEnum.SIP_VERIFY_FAILED_TITLE, lead: I18nSipSetupEnum.SIP_VERIFY_FAILED_LEAD };
		}
	}

	/** The lead with `%TIME%` filled from the lock or rate-limit time, in the customer's own zone. */
	get verifyResultLead(): string {
		const retryAt = this.store.verificationResult()?.retryAt;
		return fillPlaceholders(this.translate(this.verifyResultCopy.lead), {
			TIME: retryAt ? formatRetryTime(new Date(retryAt), this.locale ?? 'de-DE') : '',
		});
	}

	get verifyCallingLabel(): string {
		const key =
			this.verification.status() === 'ANSWERED'
				? I18nSipSetupEnum.SIP_VERIFY_STATUS_ANSWERED
				: I18nSipSetupEnum.SIP_VERIFY_STATUS_CALLING;
		return fillPlaceholders(this.translate(key), { NUMBER: this.signalledNumber ?? '' });
	}

	get autoContinueLabel(): string | null {
		const seconds = this.verification.autoContinueIn();
		if (seconds === null) {
			return null;
		}
		return fillPlaceholders(this.translate(I18nSipSetupEnum.SIP_VERIFY_AUTO_CONTINUE), { COUNT_DOWN: seconds });
	}

	/** The code as single digits for screen readers - "4 7 1 1", not "four thousand seven hundred eleven". */
	get verifyCodeSpoken(): string {
		return (this.verification.code() ?? '').split('').join(' ');
	}

	/** Any key or pointer on the confirmation screen stops its countdown. */
	onUserActivity(): void {
		if (this.store.phase() === 'verified') {
			this.verification.holdAutoContinue();
		}
	}

	get verifyValidForLabel(): string | null {
		const seconds = this.verification.secondsLeft;
		if (seconds === null) {
			return null;
		}
		const time = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
		return fillPlaceholders(this.translate(I18nSipSetupEnum.SIP_VERIFY_VALID_FOR), { TIME: time });
	}

	/** The overview's title: a booking names the SIP-Anschluss, a number-only run does not. */
	get successTitle(): I18nSipSetupEnum {
		// A link reopened after its order booked nothing in this run, yet must read as "booked".
		if (this.isLinkEntry) {
			return I18nSipSetupEnum.SIP_BOOKED_TITLE;
		}
		return this.store.sipOrdered() ? I18nSipSetupEnum.SIP_BOOKED_TITLE : I18nSipSetupEnum.SIP_SUCCESS_TITLE;
	}

	/**
	 * D6, revised 2026-09-11: the FORMAT is now checked here (`isPlausiblePhoneNumber`), because a
	 * typo becomes the caller ID on every callback. What is still NOT checked is policy —
	 * landline-only and blocked ranges remain PS-6391's. The compensating condition is unchanged:
	 * no wording in the template may claim the number is already being signalled or verified.
	 */
	finish(): void {
		const number = this.store.outgoingNumber().trim();
		if (!this.canFinish || number.length === 0) {
			return;
		}
		this.ready.emit(number);
	}

	/**
	 * Shows the failure screen. It does NOT emit yet, and that is the whole fix: emitting `failed`
	 * makes the host close the dialog, so the panel the customer is supposed to read flashed up for
	 * one frame and vanished (Torsten, 2026-09-11, on the ambiguousToken path). What they were left
	 * with was a red flicker and a closed dialog, while the only handling upstream is a log line
	 * (`onSetupFailed`).
	 *
	 * The reason is kept and emitted when they acknowledge the screen, so the diagnostic still
	 * reaches the parent and the contract with the host is unchanged.
	 */
	private fail(reason: string): void {
		this.store.failureReason.set(reason);
		this.store.phase.set('failed');
	}

	/**
	 * Sends the customer back to the sign-in, remembering where they were so the second sign-in
	 * returns them to it rather than restarting the run. The stale token goes: keeping it would let
	 * the same dead request be fired again.
	 */
	private requireNewSignIn(): void {
		// The code is gone after a new sign-in, so any verify screen resumes at its start.
		const phase = this.store.phase();
		this.verification.stop();
		this.store.resumeAfter.set(resumePhaseAfterSignIn(phase));
		this.tokenStore.clear();
		this.store.phoneServiceId.set(null);
		this.enterSignIn();
		this.store.loginError.set(I18nSipSetupEnum.SIP_LOGIN_EXPIRED);
	}

	/** A translation resolved now; the signal read makes a template binding through it reactive. */
	private translate(key: I18nSipSetupEnum): string {
		this.translations();
		return this.localization.getTranslationByKey(key);
	}

	/** Closes the run from the failure screen, handing the reason upstream on the way out. */
	acknowledgeFailure(): void {
		this.failed.emit(this.store.failureReason() ?? 'unknown');
	}
}

/** A 4xx, or a 500 from the service itself: nothing was booked. Everything else may have booked. */
function isDefiniteOrderRefusal(status: number | null): boolean {
	return status !== null && ((status >= 400 && status < 500) || status === 500);
}

/**
 * HTTP 422 with `errorCode` ADDRESS_INVALID: bookingmgmt could not use the contract address. A
 * missing, null or non-object body has no `errorCode`, so it stays a plain refusal.
 *
 * @ticket PS-6605
 */
function isAddressInvalidRefusal(error: unknown): boolean {
	return (
		error instanceof HttpErrorResponse &&
		error.status === 422 &&
		(error.error as { errorCode?: unknown } | null)?.errorCode === ORDER_ERROR_ADDRESS_INVALID
	);
}

/** What the log says about an order whose outcome is unknown - kind and status, never a body. */
function describeOrderError(error: unknown, timedOut: boolean, status: number | null): string {
	if (timedOut) {
		return 'timeout';
	}
	if (status !== null) {
		return `HTTP ${status}`;
	}
	return error instanceof Error ? error.name : 'unknown';
}

/** No answer, or one that says "later": asking again might work. */
function isTransientError(error: unknown): boolean {
	if (!(error instanceof HttpErrorResponse)) {
		return true;
	}
	return error.status === 0 || error.status === 408 || error.status === 429 || error.status >= 500;
}

/**
 * Where a run continues after a new sign-in. The code died with the old session, so any running or
 * finished attempt resumes at the verify screen; a confirmed number goes on to the overview.
 */
export function resumePhaseAfterSignIn(phase: SetupPhase): SetupPhase {
	switch (phase) {
		case 'verifyRunning':
		case 'verifyResult':
			return 'verify';
		case 'verified':
			return 'booked';
		default:
			return phase;
	}
}

/** Today: the time only. Another day: day, month and time. In the page's language, the browser's zone. */
export function formatRetryTime(at: Date, locale: string, today: Date = new Date()): string {
	const sameDay = at.toDateString() === today.toDateString();
	const options: Intl.DateTimeFormatOptions = sameDay
		? { hour: '2-digit', minute: '2-digit' }
		: { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' };
	try {
		return new Intl.DateTimeFormat(locale, options).format(at);
	} catch {
		return new Intl.DateTimeFormat('de-DE', options).format(at);
	}
}
